import API_SERVICE from '@Service/apiService';
import store from '@Store';
import { AUTHENTICATION, NOTIFICATION_HANDLER } from '@Constants/types';
import { STORAGE_TYPE, getFromStorage } from '@Service/storageService';
import { LOG_TYPE, addLog } from '@Service/loggingService';
import { submitFile } from './documentActions';
import { checkIfUserIsActive } from '@Actions/authentication';
import { getAPIByVersion } from '@Service/versionService';
import { isAfterOrEqual } from '@Utils/versionUtils';
import { calculateOverflow } from '@Utils/commonUtils';

// Fetch the version information from Seal
// Note: This major.minor.revision version number is ONLY available for "release builds" of the seal backend
//       none release builds will either return a master.buildNumber format version number, of just NULLs for all the fields
// i.e.
// Format: 7.3.1 for released versions
// Format: master.5005 for master builds
// Given that most testing cycles will include testing against the latest master build or some old master build the application
// will need to switch on a mocked version number
export default function getSealVersion() {
    try {
        let sealVersion = '';
        const mockSealVersion = getFromStorage('mockSealVersion', STORAGE_TYPE.LOCAL_STORAGE);
        if (!mockSealVersion || mockSealVersion.length === 0) {
            // mock version NOT set, so fetch version information from the backend server
            addLog(LOG_TYPE.INFO, 'sealAPIActions.getSealVersion', '/seal-ws/v4/buildinfo/release');
            API_SERVICE.refresh();
            API_SERVICE.SEAL_API({ method: 'GET', url: '/seal-ws/v4/buildinfo/release' }).then(
                ({ status, data }) => {
                    addLog(LOG_TYPE.INFO, 'sealAPIActions.getSealVersion', data);
                    if (status === 200) {
                        sealVersion = data.build_info.version;

                        // if this is a release version number it should be in the format v.v.v
                        const reg = /[0-9]+[.][0-9]+[.]+[0-9]+/;
                        if (reg.test(sealVersion)) {
                            store.dispatch({
                                type: AUTHENTICATION.SET_SEAL_VERSION,
                                payload: sealVersion
                            });
                        } else {
                            // This must be a master build, therefore the release information is not available and mock version is not set
                            store.dispatch({
                                type: AUTHENTICATION.MOCK_VERSION_NOT_SET
                            });
                        }
                    } else {
                        const errMessage = `Unexpected HTTP Status code:${status}`;
                        addLog(LOG_TYPE.ERROR, 'sealAPIActions.getSealVersion', errMessage);

                        store.dispatch({
                            type: AUTHENTICATION.GET_VERSION_FAILURE,
                            payload:
                                'There is a problem connecting to the Seal services right now. If something does not seem to work as expected please contact your system administrator.'
                        });
                    }
                }
            );
        } else {
            // mock version set
            store.dispatch({
                type: AUTHENTICATION.SET_MOCK_VERSION,
                payload: mockSealVersion
            });
        }
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'sealAPIActions.getSealVersion', err);
        store.dispatch({
            type: AUTHENTICATION.GET_VERSION_FAILURE,
            payload:
                'There is a problem connecting to the Seal services right now. If something does not seem to work as expected please contact your system administrator.'
        });
    }
}

export function getSealVersionPromise(permissions, loginStatus) {
    // eslint-disable-next-line
    const promise = new Promise((resolve, reject) => {
        try {
            let sealVersion = '';
            const mockSealVersion = getFromStorage('mockSealVersion', STORAGE_TYPE.LOCAL_STORAGE);
            if (!mockSealVersion || mockSealVersion.length === 0) {
                // mock version NOT set, so fetch version information from the backend server
                addLog(
                    LOG_TYPE.INFO,
                    'sealAPIActions.getSealVersion',
                    '/seal-ws/v4/buildinfo/release'
                );
                API_SERVICE.refresh();
                API_SERVICE.SEAL_API({ method: 'GET', url: '/seal-ws/v4/buildinfo/release' }).then(
                    ({ status, data }) => {
                        addLog(LOG_TYPE.INFO, 'sealAPIActions.getSealVersion', data);
                        if (status === 200) {
                            sealVersion = data.build_info.version;

                            // if this is a release version number it should be in the format v.v.v
                            const reg = /[0-9]+[.][0-9]+[.]+[0-9]+/;
                            if (reg.test(sealVersion)) {
                                store.dispatch({
                                    type: AUTHENTICATION.SET_SEAL_VERSION,
                                    payload: sealVersion
                                });
                                const res = { sealVersion, permissions, status, loginStatus };
                                resolve(res);
                            } else {
                                // This must be a master build, therefore the release information is not available and mock version is not set
                                resolve({ permissions, status: loginStatus });
                            }
                        } else {
                            resolve({ permissions, loginStatus });
                            const errMessage = `Unexpected HTTP Status code:${status}`;
                            addLog(LOG_TYPE.ERROR, 'sealAPIActions.getSealVersion', errMessage);

                            store.dispatch({
                                type: AUTHENTICATION.GET_VERSION_FAILURE,
                                payload:
                                    'There is a problem connecting to the Seal services right now. If something does not seem to work as expected please contact your system administrator.'
                            });
                        }
                    }
                );
            } else {
                // mock version set
                resolve({ permissions, loginStatus });
                store.dispatch({
                    type: AUTHENTICATION.SET_MOCK_VERSION,
                    payload: mockSealVersion
                });
            }
        } catch (err) {
            reject(err);
            addLog(LOG_TYPE.ERROR, 'sealAPIActions.getSealVersion', err);
            store.dispatch({
                type: AUTHENTICATION.GET_VERSION_FAILURE,
                payload:
                    'There is a problem connecting to the Seal services right now. If something does not seem to work as expected please contact your system administrator.'
            });
        }
    });
    return promise;
}

// Fetch the version information from Seal
// Note: This year"Q"Quarter.revision version number is ONLY available for "release builds" of the seal backend
//       none release builds will either return a master.buildNumber format version number, of just NULLs for all the fields
// i.e.
// Format: 2019Q3.3 for released versions
// Format: master.2371 for master builds
// Given that most testing cycles will include testing against the latest master build or some old master build the application
// will need to switch on a mocked version number
export function getSealAPIVersion() {
    try {
        addLog(LOG_TYPE.INFO, 'sealAPIActions.getSealVersion', '/seal-ws/v4/buildinfo/api');
        API_SERVICE.refresh();
        API_SERVICE.SEAL_API({ method: 'GET', url: '/seal-ws/v4/buildinfo/api' }).then(
            ({ status, data }) => {
                addLog(LOG_TYPE.INFO, 'sealAPIActions.getSealAPIVersion', data);
                if (status === 200) {
                    store.dispatch({
                        type: AUTHENTICATION.VERSION_INFO,
                        payload: data.build_info.version
                    });
                } else {
                    const errMessage = `Unexpected HTTP Status code:${status}`;
                    addLog(LOG_TYPE.ERROR, 'sealAPIActions.getSealVersion', errMessage);

                    store.dispatch({
                        type: AUTHENTICATION.GET_VERSION_FAILURE,
                        payload:
                            'There is a problem connecting to the Seal services right now. If something does not seem to work as expected please contact your system administrator.'
                    });
                }
            }
        );
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'sealAPIActions.getSealAPIVersion', err);
        store.dispatch({
            type: AUTHENTICATION.GET_VERSION_FAILURE,
            payload: `Failed to acquire version information. Error: ${err.message}`
        });
    }
}

export const submitFileWithServiceCheck = (
    digiAppId,
    digiAppName,
    currentPage
) => async dispatch => {
    API_SERVICE.refresh();
    const version = API_SERVICE.getVersion();
    addLog(LOG_TYPE.INFO, 'sealAPIActions.submitFileWithServiceCheck', `version=${version}`);
    const statusCheckEndPointUrl = getAPIByVersion('analyticsStatus', version);
    const endpointUrl = `analytics-ws${statusCheckEndPointUrl}`;

    API_SERVICE.SEAL_API({ timeout: 4000, method: 'GET', url: endpointUrl })
        .then(({ status, data }) => {
            addLog(LOG_TYPE.INFO, 'sealAPIActions.getExtractionEnabledStatus', data);
            if (status === 200) {
                let isReady = true;
                if (isAfterOrEqual('8.1.0', version)) {
                    addLog(
                        LOG_TYPE.INFO,
                        'sealAPIActions.submitFileWithServiceCheck',
                        `Analytics Status=${data.status}`
                    );

                    const { tasks, languages } = data;
                    addLog(
                        LOG_TYPE.INFO,
                        'sealAPIActions.submitFileWithServiceCheck',
                        `Tasks count=${tasks.length}`
                    );

                    addLog(
                        LOG_TYPE.INFO,
                        'sealAPIActions.submitFileWithServiceCheck',
                        `Languages count=${languages.length}`
                    );

                    // at least 1 occurence of 'ExtractionEnabled' is required for the system to be ready
                    isReady = languages.some(langItem => langItem.state === 'ExtractionEnabled');
                    if (isReady) {
                        addLog(
                            LOG_TYPE.INFO,
                            'sealAPIActions.submitFileWithServiceCheck',
                            'Found at least one language where state=ExtractionEnabled'
                        );
                    } else {
                        addLog(
                            LOG_TYPE.INFO,
                            'sealAPIActions.submitFileWithServiceCheck',
                            'No languages where state=ExtractionEnabled'
                        );
                    }

                    if (tasks.length > 0) {
                        addLog(
                            LOG_TYPE.INFO,
                            'sealAPIActions.submitFileWithServiceCheck',
                            'Active Tasks Running - Possible index rebuild in-progress'
                        );
                    }

                    // Now check for Status === Online && Task.Count === 0
                    isReady = isReady && data.status === 'Online' && tasks.length === 0;
                    addLog(
                        LOG_TYPE.INFO,
                        'sealAPIActions.submitFileWithServiceCheck',
                        `isReady = ${isReady}`
                    );
                } else {
                    // Older systems
                    const { reply } = data;
                    const { statusItems } = reply;

                    // at least 1 occurence of 'ExtractionEnabled' is required for the system to be ready
                    isReady = statusItems.some(
                        statusItem => statusItem.state === 'ExtractionEnabled'
                    );

                    if (isReady) {
                        addLog(
                            LOG_TYPE.INFO,
                            'sealAPIActions.submitFileWithServiceCheck',
                            'Found at least one language where state=ExtractionEnabled'
                        );
                    } else {
                        addLog(
                            LOG_TYPE.INFO,
                            'sealAPIActions.submitFileWithServiceCheck',
                            'No languages where state=ExtractionEnabled'
                        );
                    }

                    // any occurrences of 'InitIndexBuildPending' means that the system is NOT ready
                    isReady =
                        isReady &&
                        !statusItems.some(
                            statusItem => statusItem.state === 'InitIndexBuildPending'
                        );

                    addLog(
                        LOG_TYPE.INFO,
                        'sealAPIActions.submitFileWithServiceCheck',
                        `isReady = ${isReady}`
                    );
                }

                if (isReady) {
                    // submit document if state='ExtractionEnabled'
                    submitFile(digiAppId, digiAppName, currentPage);
                } else {
                    // otherwise throw a warning to the end user
                    dispatch({
                        type: NOTIFICATION_HANDLER.WARNING,
                        payload:
                            'The server is currently busy with rebuilding indexes and no documents can be processed until it completes. The rebuild process can take some time, please try again later.'
                    });
                    calculateOverflow('');
                }
            } else {
                if (status !== 401) {
                    const errMessage = `Unexpected HTTP Status code:${status}`;
                    forceSubmitWithWarning(
                        errMessage,
                        'sealAPIActions.submitFileWithServiceCheck',
                        digiAppId,
                        digiAppName,
                        currentPage
                    );
                }
            }
        })
        .catch(async err => {
            const isUserActive = await dispatch(checkIfUserIsActive());
            if (isUserActive)
                forceSubmitWithWarning(
                    err,
                    'sealAPIActions.getExtractionEnabledStatus',
                    digiAppId,
                    digiAppName,
                    currentPage
                );
        });
};

function forceSubmitWithWarning(err, logMessage, digiAppId, digiAppName, currentPage) {
    addLog(LOG_TYPE.ERROR, logMessage, err);
    submitFile(digiAppId, digiAppName, currentPage);

    if (err.toString().includes('403')) {
        store.dispatch({
            type: NOTIFICATION_HANDLER.WARNING,
            payload:
                'You do not have sufficient privileges to check on the status of the analytics service. Document analysis will continue anyway. Please contact your administrator for sufficient privileges.'
        });
    } else {
        store.dispatch({
            type: NOTIFICATION_HANDLER.WARNING,
            payload:
                'The analytic service is not responding properly which might cause additional delay or stop the process from completing. Please wait.'
        });
    }
}
